/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useServiceContext } from '../../root'
import useDocumentsListService from '../../hooks/useDocumentsListService'

// Role constants (matching lego app)
enum Role {
  EXTERNAL = 'EXTERNAL',
  USER = 'USER',
  ADMIN = 'ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN'
}

// Import actual section components
import {
  FavoritesDocsSection,
  RecentDocsSection,
  WorkflowsSection,
  ReportsSection,
  MyTasksSection,
  UpcomingPeriodicReviewSection
} from './sections'
import { PageHomeEos } from './eos'

interface HomePageProps {
  eosAdminReviewFlow?: boolean
  workflowInstructionsData?: {
    uuid: string
    timeStamp: string
  }
}

const HomePage: React.FC<HomePageProps> = (props) => {
  const {
    service: { init, auth, access }
  } = useServiceContext()
  const { loadPeriodicReview } = useDocumentsListService()
  const [isEosEnabled] = useState(init.getFeaturesInfo().eosFeature.show)
  const [isPeriodicReviewEnabled] = useState(
    init.getFeaturesInfo()?.allFeaturesData?.periodicReviewCustomPropEnabled || false
  )
  const [totalPeriodReviewDocs, setTotalPeriodicReviewDocs] = useState(0)
  const [isEosSiteUser, setIsEosSiteUser] = useState(false)

  useEffect(() => {
    if (props?.eosAdminReviewFlow) {
      const workflowInstructionsData = {
        uuid: props?.workflowInstructionsData?.uuid,
        lastUpdatedAt: parseInt(props?.workflowInstructionsData?.timeStamp || '0') * 1000
      }
      console.log(workflowInstructionsData)
      // TODO-EOS: Create EosWorkflowInstructionsReview component
    }

    if (isPeriodicReviewEnabled) {
      loadPeriodicReview({ pageSize: 5, pageStart: 0 }).then((data) => {
        setTotalPeriodicReviewDocs(data?.totalElements || 0)
      })
    }

    const userRole = auth.getRole()
    setIsEosSiteUser(userRole === 'EOS_SITE_USER')
  }, [])

  const isExternalUser = auth.getRole() === Role.EXTERNAL
  const isRegularUser = auth.getRole() === Role.USER
  return (
    <div className="mcc-content-frame theme-sandman">
      <div className="page-content">
        <div id="dashboard-wrapper">
          {isEosSiteUser ? (
            <>
              <PageHomeEos />
            </>
          ) : (
            <>
              <div className="row mb20">
                {isPeriodicReviewEnabled &&
                totalPeriodReviewDocs > 0 &&
                access.isAllowed('homepageAccessPeriodicReview') ? (
                  <div className={isExternalUser ? 'col-lg-12' : 'col-lg-6'}>
                    <UpcomingPeriodicReviewSection />
                  </div>
                ) : (
                  !isEosEnabled &&
                  access.isAllowed('homepageAccessRecent') && (
                    <div className={isExternalUser ? 'col-lg-12' : 'col-lg-6'}>
                      <RecentDocsSection />
                    </div>
                  )
                )}

                {access.isAllowed('homepageAccessTasks') && (
                  <div className={isExternalUser || isEosEnabled ? 'col-lg-12' : 'col-lg-6'}>
                    <MyTasksSection isEosEnabled={isEosEnabled ?? false} />
                  </div>
                )}
              </div>

              {access.isAllowed('homepageAccessWorkflows') && (
                <div className="row mb20">
                  <div className="col-xs-12">
                    <WorkflowsSection isEosEnabled={isEosEnabled ?? false} />
                  </div>
                </div>
              )}

              <div className="row mb20">
                {access.isAllowed('homepageAccessFavorites') && (
                  <div className={!access.isAllowed('homepageAccessReports') ? 'col-lg-12' : 'col-lg-6'}>
                    <FavoritesDocsSection hidePaginate={true} />
                  </div>
                )}

                {!(isRegularUser && isEosEnabled) && access.isAllowed('homepageAccessReports') && (
                  <div className="col-lg-6">
                    <ReportsSection />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default HomePage
